import { MetricFilterMapping } from '@statsbomb/parachute-types';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { isEqual } from 'lodash/fp';
import { useEffect } from 'react';
import { filtersAtom } from '@/atoms/filters/filter';
import { atomsToHydrateAtom } from '@/atoms/hydration';
import {
  appliedMappingMetricAtom,
  FiltersWhenLeft,
  filtersWhenLeftAtom,
  lastAppliedMappingAtom,
  mappedMetricFilterAtom,
  mappedMetricKeyAtom,
} from '@/atoms/metric/metricFilterMapping';
import { filterAtomDefaults } from '@/consts/defaults/filterAtomDefaults';
import { ConfigFilterAtomValues } from '@/types/userConfigs';
import { applyFilterToolCallInput } from '@/utils/applyFilterToolCallInput';

// a mapping owns the event groups and pitch zones it applies (plus positions, when it sets them)
const clearMappedFilters = (mapping: MetricFilterMapping, filters: ConfigFilterAtomValues): ConfigFilterAtomValues => ({
  ...filters,
  eventFilterGroups: filterAtomDefaults.eventFilterGroups,
  pitchStartZones: filterAtomDefaults.pitchStartZones,
  pitchEndZones: filterAtomDefaults.pitchEndZones,
  positions: mapping.positions ? filterAtomDefaults.positions : filters.positions,
});

/**
 * Clears the previous mapping's fields from `filters` then applies the next mapping.
 * Pass the same mapping for both arguments to re-apply (e.g. re-requesting the selected metric).
 */
export const replaceMappedFilters = (
  filters: ConfigFilterAtomValues,
  previousMapping: MetricFilterMapping | undefined,
  nextMapping: MetricFilterMapping | undefined,
): ConfigFilterAtomValues => {
  const cleared = previousMapping ? clearMappedFilters(previousMapping, filters) : filters;
  return nextMapping
    ? applyFilterToolCallInput({ pitchStartZones: [], pitchEndZones: [], ...nextMapping }, cleared)
    : cleared;
};

// whether the filters already carry what the mapping would apply, e.g. left in place when the vis tab was left, or
// restored by a hydration such as browser back
const hasMappedFilters = (mapping: MetricFilterMapping, filters: ConfigFilterAtomValues): boolean => {
  const applied = applyFilterToolCallInput({ ...mapping }, filters);
  const hasGroups = applied.eventFilterGroups.every(group =>
    filters.eventFilterGroups.some(existing => isEqual(existing, group)),
  );
  const hasZones =
    (!mapping.pitchStartZones?.length || isEqual(filters.pitchStartZones, applied.pitchStartZones)) &&
    (!mapping.pitchEndZones?.length || isEqual(filters.pitchEndZones, applied.pitchEndZones));
  return hasGroups && hasZones;
};

const withMappedPositions = (mapping: MetricFilterMapping, filters: ConfigFilterAtomValues): ConfigFilterAtomValues =>
  mapping.positions ? applyFilterToolCallInput({ positions: mapping.positions }, filters) : filters;

const withoutMappedPositions = (
  mapping: MetricFilterMapping,
  filters: ConfigFilterAtomValues,
): ConfigFilterAtomValues =>
  mapping.positions && isEqual(withMappedPositions(mapping, filters).positions, filters.positions)
    ? { ...filters, positions: filterAtomDefaults.positions }
    : filters;

const resolveMappedFilters = (
  filters: ConfigFilterAtomValues,
  previousMapping: MetricFilterMapping | undefined,
  mapping: MetricFilterMapping | undefined,
  filtersWhenLeft: FiltersWhenLeft | undefined,
): ConfigFilterAtomValues => {
  // nothing replaced the groups and zones since the page was left, so what the user changed since the mapping was
  // applied is kept, even edits to the mapped groups
  const untouchedSinceLeft =
    previousMapping === mapping &&
    filtersWhenLeft?.eventFilterGroups === filters.eventFilterGroups &&
    filtersWhenLeft.pitchStartZones === filters.pitchStartZones &&
    filtersWhenLeft.pitchEndZones === filters.pitchEndZones;
  const alreadyApplied =
    !!mapping &&
    (untouchedSinceLeft || ((!previousMapping || previousMapping === mapping) && hasMappedFilters(mapping, filters)));
  return alreadyApplied
    ? withMappedPositions(mapping, filters)
    : replaceMappedFilters(filters, previousMapping, mapping);
};

/**
 * Applies the selected metric's mapped filters whenever a mapped metric is selected, clearing whatever the previous
 * mapping applied. Unmapped metrics leave the filters alone, so the vis page falls back to the metric endpoint.
 *
 * Leaving the vis page keeps the mapped event groups and pitch zones in the filters, like any filter set through the
 * UI controls, and coming back with the same metric leaves them alone. Only a change of metric resets them.
 */
export const useApplyMetricFilterMapping = (): void => {
  const store = useStore();
  const mapping = useAtomValue(mappedMetricFilterAtom);
  const metric = useAtomValue(mappedMetricKeyAtom);
  const atomsToHydrate = useAtomValue(atomsToHydrateAtom);
  const setAppliedMetric = useSetAtom(appliedMappingMetricAtom);
  const setFilters = useSetAtom(filtersAtom);

  useEffect(() => {
    // a pending hydration (e.g. the preview's fullscreen link) overwrites the event groups and pitch zones when it
    // lands, and can land after this effect; wait for it to clear, then apply
    if (atomsToHydrate) {
      setAppliedMetric(null);
      return;
    }

    // remembered across visits: the mapping may have changed while the vis page was not mounted
    const previousMapping = store.get(lastAppliedMappingAtom);
    store.set(lastAppliedMappingAtom, mapping);
    const filtersWhenLeft = store.get(filtersWhenLeftAtom);
    store.set(filtersWhenLeftAtom, undefined);

    if (previousMapping || mapping) {
      setFilters(prev => resolveMappedFilters(prev, previousMapping, mapping, filtersWhenLeft));
    }
    setAppliedMetric(mapping && metric ? metric : null);
  }, [store, mapping, metric, atomsToHydrate, setFilters, setAppliedMetric]);

  useEffect(
    () => () => {
      setAppliedMetric(null);
      // positions apply on every tab, so the ones the mapping set must not follow the user off the vis page
      const leavingMapping = store.get(lastAppliedMappingAtom);
      if (!leavingMapping) return;
      const { eventFilterGroups, pitchStartZones, pitchEndZones } = store.get(filtersAtom);
      store.set(filtersWhenLeftAtom, { eventFilterGroups, pitchStartZones, pitchEndZones });
      setFilters(prev => withoutMappedPositions(leavingMapping, prev));
    },
    [store, setAppliedMetric, setFilters],
  );
};
